import { SportVenueAssetWorkingHours } from "../../app/models/SportVenueAssetWorkingHours";
import { SportVenueWorkingHours } from "../../app/models/SportVenueWorkingHours";
import i18n from "../../i18n";

// Type union for both working hours types
type WorkingHours = SportVenueWorkingHours | SportVenueAssetWorkingHours;

// Helper function to display working hours summary in table
export const getWorkingHoursSummary = (
  workingHours?: WorkingHours[]
): string => {
  if (!workingHours || workingHours.length === 0) {
    return (i18n as any).t('workingHours.summary.notSet');
  }

  const DAYS = [
    (i18n as any).t('workingHours.days.sun'),
    (i18n as any).t('workingHours.days.mon'),
    (i18n as any).t('workingHours.days.tue'),
    (i18n as any).t('workingHours.days.wed'),
    (i18n as any).t('workingHours.days.thu'),
    (i18n as any).t('workingHours.days.fri'),
    (i18n as any).t('workingHours.days.sat')
  ];
  
  // Create a complete week schedule (initialize all days as closed)
  const weekSchedule: { [key: number]: WorkingHours } = {};
  
  // Fill in the actual working hours
  workingHours.forEach(wh => {
    weekSchedule[wh.dayOfWeek] = wh;
  });

  // Group by similar schedules
  const scheduleGroups: { [key: string]: number[] } = {};
  
  for (let day = 0; day < 7; day++) {
    const wh = weekSchedule[day];
    let scheduleKey: string;
    
    if (!wh || wh.isClosed) {
      scheduleKey = (i18n as any).t('workingHours.summary.closed');
    } else if (wh.openTime && wh.closeTime) {
      scheduleKey = `${wh.openTime}-${wh.closeTime}`;
      if (wh.breakStart && wh.breakEnd) {
        scheduleKey += ` (${(i18n as any).t('workingHours.summary.break')}: ${wh.breakStart}-${wh.breakEnd})`;
      }
    } else {
      scheduleKey = (i18n as any).t('workingHours.closed');
    }
    
    if (!scheduleGroups[scheduleKey]) {
      scheduleGroups[scheduleKey] = [];
    }
    scheduleGroups[scheduleKey].push(day);
  }

  // Format the grouped schedules
  const formattedSchedules: string[] = [];
  
  Object.entries(scheduleGroups).forEach(([schedule, days]) => {
    const dayRanges = formatDayRanges(days, DAYS);
    formattedSchedules.push(`${dayRanges}: ${schedule}`);
  });

  return formattedSchedules.join(' | ');
};

// Helper function to format day ranges (e.g., "Mon-Fri", "Sat-Sun")
const formatDayRanges = (days: number[], DAYS: string[]): string => {
  if (days.length === 0) return '';
  
  days.sort((a, b) => a - b);
  
  const ranges: string[] = [];
  let start = days[0];
  let end = days[0];
  
  for (let i = 1; i < days.length; i++) {
    if (days[i] === end + 1) {
      end = days[i];
    } else {
      // Add the current range
      if (start === end) {
        ranges.push(DAYS[start]);
      } else if (end === start + 1) {
        ranges.push(`${DAYS[start]}, ${DAYS[end]}`);
      } else {
        ranges.push(`${DAYS[start]}-${DAYS[end]}`);
      }
      start = days[i];
      end = days[i];
    }
  }
  
  // Add the final range
  if (start === end) {
    ranges.push(DAYS[start]);
  } else if (end === start + 1) {
    ranges.push(`${DAYS[start]}, ${DAYS[end]}`);
  } else {
    ranges.push(`${DAYS[start]}-${DAYS[end]}`);
  }
  
  return ranges.join(', ');
};

// More detailed working hours display for tooltips or detailed views
export const getDetailedWorkingHours = (
  workingHours?: WorkingHours[]
): string => {
  if (!workingHours || workingHours.length === 0) {
    return (i18n as any).t('workingHours.summary.notConfigured');
  }

  const DAYS = [
    (i18n as any).t('workingHours.days.sunday'),
    (i18n as any).t('workingHours.days.monday'),
    (i18n as any).t('workingHours.days.tuesday'),
    (i18n as any).t('workingHours.days.wednesday'),
    (i18n as any).t('workingHours.days.thursday'),
    (i18n as any).t('workingHours.days.friday'),
    (i18n as any).t('workingHours.days.saturday')
  ];
  
  const weekSchedule: { [key: number]: WorkingHours } = {};
  
  workingHours.forEach(wh => {
    weekSchedule[wh.dayOfWeek] = wh;
  });

  const details: string[] = [];
  
  for (let day = 0; day < 7; day++) {
    const wh = weekSchedule[day];
    const dayName = DAYS[day];
    
    if (!wh || wh.isClosed) {
      details.push(`${dayName}: ${(i18n as any).t('workingHours.summary.closed')}`);
    } else if (wh.openTime && wh.closeTime) {
      let timeStr = `${dayName}: ${wh.openTime} - ${wh.closeTime}`;
      if (wh.breakStart && wh.breakEnd) {
        timeStr += ` (${(i18n as any).t('workingHours.summary.break')}: ${wh.breakStart} - ${wh.breakEnd})`;
      }
      details.push(timeStr);
    } else {
      details.push(`${dayName}: ${(i18n as any).t('workingHours.summary.closed')}`);
    }
  }
  
  return details.join('\n');
};

// Check if venue/asset is currently open
export const isCurrentlyOpen = (
  workingHours?: WorkingHours[]
): boolean => {
  if (!workingHours || workingHours.length === 0) {
    return false;
  }

  const now = new Date();
  const currentDay = now.getDay(); // 0 = Sunday, 6 = Saturday
  const currentTime = now.toTimeString().slice(0, 5); // HH:MM format
  
  const todaySchedule = workingHours.find(wh => wh.dayOfWeek === currentDay);
  
  if (!todaySchedule || todaySchedule.isClosed || !todaySchedule.openTime || !todaySchedule.closeTime) {
    return false;
  }
  
  // Check if current time is within opening hours
  const isWithinMainHours = currentTime >= todaySchedule.openTime && currentTime <= todaySchedule.closeTime;
  
  // Check if it's during break time
  if (todaySchedule.breakStart && todaySchedule.breakEnd) {
    const isDuringBreak = currentTime >= todaySchedule.breakStart && currentTime <= todaySchedule.breakEnd;
    return isWithinMainHours && !isDuringBreak;
  }
  
  return isWithinMainHours;
};

// Get next opening time
export const getNextOpeningTime = (
  workingHours?: WorkingHours[]
): string => {
  if (!workingHours || workingHours.length === 0) {
    return (i18n as any).t('workingHours.summary.notAvailable');
  }

  const now = new Date();
  const currentDay = now.getDay();
  const currentTime = now.toTimeString().slice(0, 5);
  
  // Check today first
  const todaySchedule = workingHours.find(wh => wh.dayOfWeek === currentDay);
  if (todaySchedule && !todaySchedule.isClosed && todaySchedule.openTime && currentTime < todaySchedule.openTime) {
    return (i18n as any).t('workingHours.summary.opensToday', { time: todaySchedule.openTime });
  }
  
  // Check next 7 days
  for (let i = 1; i <= 7; i++) {
    const checkDay = (currentDay + i) % 7;
    const daySchedule = workingHours.find(wh => wh.dayOfWeek === checkDay);
    
    if (daySchedule && !daySchedule.isClosed && daySchedule.openTime) {
      const DAYS = [
        (i18n as any).t('workingHours.days.sunday'),
        (i18n as any).t('workingHours.days.monday'),
        (i18n as any).t('workingHours.days.tuesday'),
        (i18n as any).t('workingHours.days.wednesday'),
        (i18n as any).t('workingHours.days.thursday'),
        (i18n as any).t('workingHours.days.friday'),
        (i18n as any).t('workingHours.days.saturday')
      ];
      const dayName = i === 1 ? (i18n as any).t('workingHours.days.tomorrow') : DAYS[checkDay];
      return (i18n as any).t('workingHours.summary.opensOn', { day: dayName, time: daySchedule.openTime });
    }
  }
  
  return (i18n as any).t('workingHours.summary.notAvailable');
};